import * as React from "react";
import * as _ from 'lodash';
import * as moment from "moment";
import { IDonor } from "@nkr/models/index";
import { IInactiveDonor } from "./inactive-donor";
import { DonorAction } from "./donor-action";
import { IValidationError } from "./validation-error";
import { TableWrapper } from "../styles";
import { Alert, Modal, Button } from "react-bootstrap";
import Row from "./row";
import BusinessDayAlert from '../../../../../donors/profile/business-date-alert';
import DonationRangeAlert from "@nkr/views/donors/profile/donation-range-alert";
import { DonationType, isChainStarter } from "@nkr/enums/donation-type";
import { Utility } from "@nkr/services/utility";



interface IProps {
    show: boolean,
    items: IDonor[],
    isAdmin: boolean,
    onClose: () => void,
    onUpdate: (updates: { donorId: number, donationStartDate: string, donationEndDate: string }[], deletes: number[]) => void
}

interface IState {
    donors: IInactiveDonor[],
    validationErrors: IValidationError[],
    showBusinessDayAlert: boolean,
    showDonationRangeAlert: boolean,
    currentDateInput: string,
    previousDateValue: { donorId: number; name: string; value: string } | null,
    alertType: "businessDay" | "pastDate",
    donationType: number,
    isChainStarter: boolean,
    totalBusinessDays: number,
    startExportDateInput: string,
    donationStartDateInput: string,
    donationEndDateInput: string,
    endBeforeStart: boolean,
    disableSubmit: boolean;
}

class InactiveDonorVerification extends React.Component<IProps, IState> {
    readonly state: IState = {
        donors: [],
        validationErrors: [],
        showBusinessDayAlert: false,
        showDonationRangeAlert: false,
        currentDateInput: '',
        previousDateValue: null,
        alertType: "businessDay",
        donationType: null,
        totalBusinessDays: 0,
        isChainStarter: false,
        startExportDateInput: '',
        donationStartDateInput: '',
        donationEndDateInput: '',
        endBeforeStart: false,
        disableSubmit: true
    };


    componentWillReceiveProps = (nextProps: IProps) => {
        if (nextProps.show) {
            let donors = nextProps.items.map(donor => {
                return {
                    id: donor.id,
                    alias: donor.alias,
                    birthDate: donor.dateOfBirth,
                    evaluationDate: donor.dateOfEvaluation,
                    isChainStarter: isChainStarter(donor.donationTypeId),
                    donationType: donor.donationTypeId,
                    donationStartDate: donor.donationStartDate,
                    donationEndDate: donor.donationEndDate,
                    action: DonorAction["- select -"]
                };
            });

            this.setState({
                donors,
                disableSubmit: this.updateDisableSubmit(donors)
            });
        }
    }

    handleCloseBusinessDayAlert = () => {
        this.setState({
            showBusinessDayAlert: false
        });
    }

    handleCloseDonationRangeAlert = () => {
        this.setState({
            showDonationRangeAlert: false
        });
    }

    updateDisableSubmit = (donors: IInactiveDonor[]) => {
        const disableSubmit =
            donors.filter(x =>
                x.action == DonorAction.Update ||
                x.action == DonorAction.Delete
            ).length == 0;

        return disableSubmit;
    };


    handleUpdateDonationDate = (donorId: number, e: React.FormEvent<HTMLInputElement>) => {
        const target = e.currentTarget as HTMLInputElement;
        const property = target.name;
        let value = target.value;

        const donors = this.state.donors.map(donor => {
            if (donor.id === donorId) {
                // Allow clearing the field even for Admin
                if (!value) {
                    return {
                        ...donor,
                        [property]: ""
                    };
                }

                // Format non-empty value
                const formatted = moment(value, "YYYY-MM-DD").format("MM/DD/YYYY");
                return {
                    ...donor,
                    [property]: formatted
                };
            }
            return donor;
        });

        // Admin bypass: skip validation but still update state
        if (this.props.isAdmin) {
            this.setState({
                donors,
                disableSubmit: false
            });
            return;
        }

        // Normal users: update state and run validations
        this.setState({
            donors,
            disableSubmit: this.updateDisableSubmit(donors)
        });
    };



    handleRevertDate = (donorId: number) => {
        if (this.props.isAdmin) return;
        this.setState({ disableSubmit: true });
    };


    handleDateFocus = (donorId: number, e: React.FocusEvent<any>) => {
        const target = e.currentTarget as HTMLInputElement;
        const name = target.name;
        const value = target.value || "";

        this.setState({
            previousDateValue: { donorId, name, value }
        });
    };



    handleDateBlur = (donorId: number, e: React.FormEvent<HTMLInputElement>) => {

        if (this.props.isAdmin) {
            const donors = this.state.donors;
            this.setState({
                disableSubmit: false
            });

            return;
        }

        const target = e.currentTarget as HTMLInputElement;
        const name = target.name;
        const value = target.value;

        if (!value) return;

        const selectedDate = moment(value, "YYYY-MM-DD", true);
        if (!selectedDate.isValid()) return;

        const today = moment().startOf("day");

        let showBusinessDayAlert = false;
        let showDonationRangeAlert = false;
        let currentDateInput = "";
        let startExportDateInput = "";
        let donationStartDateInput = "";
        let donationEndDateInput = "";
        let donationType = this.state.donationType;
        let isChainStarter = this.state.isChainStarter;
        let alertType: "businessDay" | "pastDate" = "businessDay";
        let endBeforeStart = false;
        let totalBusinessDays = 0;

        const donors = this.state.donors.map(donor => {

            if (donor.id !== donorId) return donor;

            const startDate =
                name == "donationStartDate"
                    ? selectedDate
                    : moment(donor.donationStartDate, "MM/DD/YYYY");

            const endDate =
                name == "donationEndDate"
                    ? selectedDate
                    : moment(donor.donationEndDate, "MM/DD/YYYY");


            if (startDate.isValid() && endDate.isValid() && endDate.isBefore(startDate)) {
                endBeforeStart = true;
                return { ...donor };
            }

            donationStartDateInput = startDate.isValid() ? startDate.format("MM/DD/YYYY") : this.state.donationStartDateInput;
            donationEndDateInput = endDate.isValid() ? endDate.format("MM/DD/YYYY") : this.state.donationEndDateInput;
            isChainStarter = donor.isChainStarter;
            donationType = donor.donationType;
            // validate start date
            if (startDate.isValid()) {

                if (startDate.isBefore(today)) {
                    showBusinessDayAlert = true;
                    alertType = "pastDate";
                    currentDateInput = startDate.format("MM/DD/YYYY");
                    return { ...donor };
                }

                if (startDate.day() == 0 || startDate.day() == 6) {
                    showBusinessDayAlert = true;
                    alertType = "businessDay";
                    currentDateInput = startDate.format("MM/DD/YYYY");
                    return { ...donor };
                }

                const exportableDate = moment();
                startExportDateInput = Utility.addBusinessDays(exportableDate, 15).format("MM/DD/YYYY");
                const businessDaysFromExport = this.getBusinessDays(exportableDate, startDate);
                totalBusinessDays = businessDaysFromExport;

                if (donor.isChainStarter) {
                    if (businessDaysFromExport < 15) {
                        showDonationRangeAlert = true;
                        return { ...donor };
                    }
                }
            }

            // range validation
            if (startDate.isValid() && endDate.isValid()) {
                const bdCount = this.getBusinessDays(startDate, endDate);

                switch (donor.donationType) {
                    case DonationType.StandardVoucher:
                        if (bdCount < 15 || bdCount > 30) {
                            showDonationRangeAlert = true;
                            totalBusinessDays = bdCount;
                        }
                        break;

                    case DonationType.FamilyVoucher:
                    case DonationType.NonDirectedDonor:
                        if (bdCount < 5 || bdCount > 15) {
                            showDonationRangeAlert = true;
                            totalBusinessDays = bdCount;
                        }
                        break;
                }
            }

            return { ...donor };
        });


        this.setState({
            donors,
            currentDateInput,
            startExportDateInput,
            showBusinessDayAlert,
            showDonationRangeAlert,
            alertType,
            endBeforeStart,
            donationStartDateInput,
            donationEndDateInput,
            donationType,
            totalBusinessDays,
            isChainStarter,
            disableSubmit: showDonationRangeAlert || showBusinessDayAlert || endBeforeStart
        });
    };


    getBusinessDays = (startDate: moment.Moment | string, endDate: moment.Moment | string): number => {
        let start = moment(startDate).startOf('day');
        let end = moment(endDate).startOf('day');

        if (end.isBefore(start)) {
            [start, end] = [end, start];
        }

        let businessDays = 0;

        while (start.isSameOrBefore(end)) {
            const day = start.day();

            if (day !== 0 && day !== 6) {
                businessDays++;
            }

            start.add(1, 'day');
        }

        return businessDays;
    };


    addBusinessDays = (datePassed: moment.MomentInput, xAmountDays: number) => {
        let date = moment(datePassed);
        let addedDays = 0;

        while (addedDays < xAmountDays) {
            date = date.add(1, "day");

            const day = date.day();
            if (day !== 0 && day !== 6) {
                addedDays++;
            }
        }

        return date.subtract(1, "day");
    }



    handleUpdateAction = (donorId: number, action: DonorAction) => {
        let donors = this.state.donors.map(donor => {
            if (donor.id == donorId) {
                donor.action = action;
            }

            return _.cloneDeep(donor);
        });

        this.setState({
            donors,
            disableSubmit: this.updateDisableSubmit(donors)
        });
    }

    handleSave = () => {
        let validationErrors = this.validate();

        this.setState({
            validationErrors
        }, () => {
            if (validationErrors.length == 0) {
                let updates = this.state.donors.filter(x => x.action == DonorAction.Update).map(x => {
                    let { id: donorId, donationStartDate, donationEndDate } = x;

                    return {
                        donorId,
                        donationStartDate,
                        donationEndDate
                    };
                });

                let deletes = this.state.donors.filter(x => x.action == DonorAction.Delete).map(x => x.id);

                this.props.onUpdate(updates, deletes);
            }
        });
    }

    validate = () => {
        let errors: IValidationError[] = [];

        this.state.donors.forEach((x, index) => {
            let error = { index, messages: [] };

            let { id: donorId, donationStartDate, donationEndDate, action } = x;
            const exportableDate = moment();
            const businessDaysFromExport = this.getBusinessDays(exportableDate, moment(donationStartDate));

            if (action == DonorAction.Update) {
                if (donationStartDate == '' || donationEndDate == '') {
                    error.messages.push('Please specify the donation start and end dates');
                } else if (moment(donationStartDate) > moment(donationEndDate)) {
                    error.messages.push('The donation start date must come before the end date');
                } else if (businessDaysFromExport < 15) {
                    error.messages.push('The minimum donation start date must be 15 business days from today');
                }

                if (error.messages.length > 0) {
                    errors.push(error);
                }
            }
        });

        return errors;
    }

    render = () => {

        const disableSubmit = this.state.donors.filter(x => x.action == DonorAction.Update || x.action == DonorAction.Delete).length == 0 || this.state.disableSubmit;

        return (<>
            <Modal show={this.props.show} bsSize='lg' backdrop='static' onHide={this.props.onClose}>
                <Modal.Header closeButton>
                    <Modal.Title>OTHER - Inactive Donor Verification</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Alert bsStyle='info'>The following are chain starters that are stalled and their review data is older than one month. </Alert>

                    {this.state.validationErrors.length > 0 && (
                        <Alert bsStyle='danger'>
                            <div>We are not able to update the donor records, please see the errors below...</div>
                        </Alert>
                    )}

                    <TableWrapper className="table table-condensed table-striped table-bordered">
                        <thead>
                            <tr>
                                <th>Donor</th>
                                <th>DoB</th>
                                <th>Evaluation Date</th>
                                <th>Donation Start Date</th>
                                <th>Donation End  Date</th>
                                <th style={{ width: '18%' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {this.state.donors.map((donor, index) =>
                                <Row
                                    key={'idvr-' + index}
                                    data={donor}
                                    index={index}
                                    error={this.state.validationErrors.find((x, errorIndex) => errorIndex == index)}
                                    disableActions={this.state.disableSubmit}
                                    onUpdateAction={(donorId, action) => this.handleUpdateAction(donorId, action)}
                                    onDateFocus={this.handleDateFocus}
                                    onDateChange={this.handleUpdateDonationDate}
                                    onDateBlur={this.handleDateBlur}
                                />
                            )}
                        </tbody>
                    </TableWrapper>
                </Modal.Body>

                <Modal.Footer>
                    <Button bsStyle="primary" disabled={disableSubmit} onClick={this.handleSave}>Submit Changes</Button>
                    <Button onClick={this.props.onClose}>Cancel</Button>
                </Modal.Footer>
            </Modal>

            <BusinessDayAlert
                show={this.state.showBusinessDayAlert}
                datechosen={this.state.currentDateInput}
                alertType={this.state.alertType}
                onClose={() => {
                    this.handleCloseBusinessDayAlert();
                    if (this.state.previousDateValue) {
                        this.handleRevertDate(this.state.previousDateValue.donorId);
                    }
                }}
            />

            <DonationRangeAlert
                show={this.state.showDonationRangeAlert}
                startExportDate={this.state.startExportDateInput}
                donationStartDate={this.state.donationStartDateInput}
                donationEndDate={this.state.donationEndDateInput}
                donationType={this.state.donationType}
                isChainStarter={this.state.isChainStarter}
                totalBusinessDays={this.state.totalBusinessDays}
                onClose={() => {
                    this.handleCloseDonationRangeAlert();
                    if (this.state.previousDateValue) {
                        this.handleRevertDate(this.state.previousDateValue.donorId);
                    }
                }}
            />
        </>);
    }
}

export default InactiveDonorVerification;